import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dropdown_black from "../../../../../../assets/AccountingModule/drop_down_icon.svg";
import color from "../../../../../../constants/color";
import styles from "./style";

const EditNotesPage = ({
  customerList,
  itemList,
  getEditNotesData,
  loading,
  editNoteData,
  updateNotes,
}) => {
  const [customer, setCustomer] = useState(editNoteData?.customer || null);
  const [items, setItems] = useState(
    editNoteData?.itemInformation?.length > 0
      ? editNoteData.itemInformation.map((item) => ({
        name: item.name || "",
        itemId: item.itemId || "",
        description: "",
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        amount: item.amount || 0,
        account: item.account || "",
        invoiceQuantity: item.quantity || 1,
      }))
      : [
        {
          name: "",
          itemId: "",
          description: "",
          quantity: "",
          rate: "",
          amount: "",
        },
      ]
  );

  const [customerId, setCustomerId] = useState(
    editNoteData?.customer?._id || ""
  );
  const [subject, setSubject] = useState(editNoteData?.subject || "");
  const [salesperson, setSalesperson] = useState(
    editNoteData?.salesperson || ""
  );
  const [customerNotes, setCustomerNotes] = useState(
    editNoteData?.customerNotes || ""
  );
  const [discountPercentage, setDiscountPercentage] = useState(
    editNoteData?.discountPercentage || 0
  );
  const [taxPercentage, setTaxPercentage] = useState(
    editNoteData?.taxPercentage || 0
  );
  const [createNoteNumber, setCreditNoteNumber] = useState(
    editNoteData?.creditNoteNumber || ""
  );
  const [createNoteDate, setCreditNoteDate] = useState(
    editNoteData?.creditNoteDate ? dayjs(editNoteData.creditNoteDate) : null
  );

  // Add error state
  const [errors, setErrors] = useState({
    customer: '',
    creditNoteNumber: '',
    creditNoteDate: '',
    salesperson: '',
    subject: '',
    items: [],
    discount: '',
    tax: ''
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { item } = location.state || {};

  useEffect(() => {
    if (editNoteData) {
      setCustomer(editNoteData.customer || null);
      setCustomerId(editNoteData.customer?._id || "");
      setCreditNoteNumber(editNoteData.creditNoteNumber || "");
      setCreditNoteDate(
        editNoteData.creditNoteDate ? dayjs(editNoteData.creditNoteDate) : null
      );
      setSalesperson(editNoteData.salesperson || "");
      setSubject(editNoteData.subject || "");
      setCustomerNotes(editNoteData.customerNotes || "");
      setDiscountPercentage(editNoteData.discountPercentage || 0);
      setTaxPercentage(editNoteData.taxPercentage || 0);

      if (editNoteData.itemInformation?.length > 0) {
        setItems(
          editNoteData.itemInformation.map((item) => ({
            name: item.name || "",
            itemId: item.itemId || "",
            description: "",
            quantity: item.quantity || 1,
            rate: item.rate || 0,
            amount: item.amount || 0,
            account: item.account || "",
            invoiceQuantity: item.quantity || 1,
          }))
        );

        // Initialize item errors
        setErrors(prev => ({
          ...prev,
          items: editNoteData.itemInformation.map(() => ({
            name: '',
            account: '',
            quantity: '',
            rate: ''
          }))
        }));
      }
    }
  }, [editNoteData]);

  useEffect(() => {
    if (item?._id) {
      getEditNotesData(item._id);
    }
  }, [item?._id]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: "",
        itemId: "",
        description: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ]);

    // Add error object for new item
    setErrors(prev => ({
      ...prev,
      items: [...prev.items, {
        name: '',
        account: '',
        quantity: '',
        rate: ''
      }]
    }));
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = [...items];
      updatedItems.splice(index, 1);
      setItems(updatedItems);

      // Remove error object for removed item
      const updatedErrors = { ...errors };
      updatedErrors.items.splice(index, 1);
      setErrors(updatedErrors);
    }
  };

  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'customer':
        return !value ? 'Customer is required' : '';
      case 'creditNoteNumber':
        return !value ? 'Credit Note Number is required' : '';
      case 'creditNoteDate':
        return !value ? 'Credit Note Date is required' : '';
      case 'salesperson':
        return !value ? 'Salesperson is required' : '';
      case 'subject':
        return !value ? 'Subject is required' : '';
      case 'itemName':
        return !value ? 'Item name is required' : '';
      case 'itemAccount':
        return !value ? 'Account is required' : '';
      case 'itemQuantity':
        return !value || value <= 0 ? 'Quantity must be greater than 0' : '';
      case 'itemRate':
        return !value || value < 0 ? 'Rate cannot be negative' : '';
      case 'discount':
        return value < 0 || value > 100 ? 'Discount must be between 0 and 100' : '';
      case 'tax':
        return value < 0 || value > 100 ? 'Tax must be between 0 and 100' : '';
      default:
        return '';
    }
  };

  const handleItemChange = (value, index, key) => {
    const updatedItems = [...items];

    if ((key === "quantity" || key === "rate") && Number(value) < 0) {
      return;
    }

    if (key === "quantity" || key === "rate" || key === "invoiceQuantity") {
      updatedItems[index][key] = Number(value);
    } else {
      updatedItems[index][key] = value;
    }

    if (key === "quantity" || key === "rate" || key === "invoiceQuantity") {
      updatedItems[index].amount =
        (updatedItems[index].invoiceQuantity || 0) *
        (updatedItems[index].rate || 0);
    }

    if (key === "name") {
      const selectedItem = itemList?.find((item) => item.title === value);
      if (selectedItem) {
        updatedItems[index].itemId = selectedItem._id || "";
        updatedItems[index].rate = Number(selectedItem.sellingPrice || 0);
        updatedItems[index].quantity = Number(selectedItem.quantity || 0);
        updatedItems[index].description = selectedItem.description || "";
        updatedItems[index].amount =
          selectedItem.sellingPrice * selectedItem.quantity;
      }
    }

    setItems(updatedItems);

    // Validate the changed field
    let errorField = '';
    let errorType = '';

    switch (key) {
      case 'name':
        errorField = 'name';
        errorType = 'itemName';
        break;
      case 'account':
        errorField = 'account';
        errorType = 'itemAccount';
        break;
      case 'invoiceQuantity':
        errorField = 'quantity';
        errorType = 'itemQuantity';
        break;
      case 'rate':
        errorField = 'rate';
        errorType = 'itemRate';
        break;
      default:
        return;
    }

    const error = validateField(errorType, value);
    setErrors(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [errorField]: error } : item
      )
    }));

    calculateSubTotal();
  };

  const calculateSubTotal = () => {
    const total = items.reduce((sum, item) => {
      const amount =
        (Number(item.invoiceQuantity) || 0) * (Number(item.rate) || 0);
      return sum + amount;
    }, 0);
    return Number(total.toFixed(2)) || 0;
  };

  const calculateDiscountValue = () => {
    const subTotal = calculateSubTotal();
    const discount = (subTotal * (Number(discountPercentage) || 0)) / 100;
    return Number(discount.toFixed(2)) || 0;
  };

  const calculateTaxValue = () => {
    const subTotal = calculateSubTotal();
    const discountedAmount = subTotal - calculateDiscountValue();
    const tax = (discountedAmount * (Number(taxPercentage) || 0)) / 100;
    return Number(tax.toFixed(2)) || 0;
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const total = subTotal - discountValue + taxValue;
    return Number(total.toFixed(2)) || 0;
  };

  const CustomDropdownIcon = () => (
    <img
      src={dropdown_black}
      alt="dropdown"
      height={22}
      width={22}
      style={{ paddingRight: "10px" }}
    />
  );

  const handleSubmit = (status) => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const grandTotal = calculateGrandTotal();

    const payload = {
      customer: customerId,
      creditNoteNumber: createNoteNumber,
      creditNoteDate: createNoteDate ? createNoteDate.toISOString() : null,
      salesperson,
      subject,
      customerNotes,
      itemInformation: items.map(item => ({
        itemId: item.itemId,
        name: item.name,
        account: item.account,
        quantity: Number(item.invoiceQuantity) || 0,
        rate: Number(item.rate) || 0,
        amount: Number(item.amount) || 0,
      })),
      total: subTotal,
      discountPercentage,
      discountValue,
      taxPercentage,
      taxValue,
      grandTotal,
      status: status || 'Draft'
    };

    updateNotes(payload, item._id);
  };

  const handleSaveDraft = () => handleSubmit('Draft');
  const handleSaveAndPublish = () => handleSubmit('Open');

  // Style for error fields
  const errorFieldStyle = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-error fieldset': {
        borderColor: 'red',
        borderWidth: '2px',
      },
    },
  };
  return (
    <>
      <Box px="20px" mt={2}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>Edit Credit Notes</Typography>
        </Box>
      </Box>
      <Box sx={styles.container}>
        {/* Form Section */}
        <Box sx={styles.formSection}>
          <Grid container spacing={2}>
            {/* First Row */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Customer Name</Typography>
                <Autocomplete
                  id="customer-select"
                  options={[
                    { _id: "add_new", name: "+ New Customer" },
                    ...(customerList || []),
                  ]}
                  getOptionLabel={(option) => option?.name || ""}
                  value={customer}
                  onChange={(_, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewCustomer"); // redirect page
                      return;
                    }
                    setCustomer(newValue);
                    setCustomerId(newValue?._id || "");
                    const error = validateField('customer', newValue?._id || '');
                    setErrors(prev => ({ ...prev, customer: error }));
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Customer"
                      size="small"
                      sx={{ ...styles.textField, ...errorFieldStyle }}
                      error={!!errors.customer}
                      helperText={errors.customer}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Credit Note#</Typography>
                <TextField
                  fullWidth
                  placeholder="Credit Note"
                  size="small"
                  sx={{ ...styles.textField, ...errorFieldStyle }}
                  value={createNoteNumber}
                  onChange={(e) => {
                    setCreditNoteNumber(e.target.value);
                    const error = validateField('creditNoteNumber', e.target.value);
                    setErrors(prev => ({ ...prev, creditNoteNumber: error }));
                  }}
                  error={!!errors.creditNoteNumber}
                  helperText={errors.creditNoteNumber}
                />
              </Box>
            </Grid>

            {/* Second Row */}

            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Credit Note Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      ...styles.datePickerOuter,
                      borderRadius: "8px !important",
                      position: 'relative'
                    }}
                  >
                    <DatePicker
                      value={createNoteDate}
                      onChange={(newValue) => {
                        setCreditNoteDate(newValue);
                        const error = validateField('creditNoteDate', newValue);
                        setErrors(prev => ({ ...prev, creditNoteDate: error }));
                      }}
                      format="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            ...styles.datePicker,
                            ...errorFieldStyle,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                              overflow: "hidden",
                            },
                            "& .MuiInputAdornment-root": {
                              paddingLeft: "5px",
                            },
                          }}
                          error={!!errors.creditNoteDate}
                        />
                      )}
                    />
                    {errors.creditNoteDate && (
                      <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5, position: 'absolute', bottom: '-20px', left: '0' }}>
                        {errors.creditNoteDate}
                      </Typography>
                    )}
                  </Box>
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "2.5em",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Typography sx={styles.label}>Salesperson</Typography>
                  <Select
                    value={salesperson}
                    onChange={(e) => {
                      setSalesperson(e.target.value);
                      const error = validateField('salesperson', e.target.value);
                      setErrors(prev => ({ ...prev, salesperson: error }));
                    }}
                    error={!!errors.salesperson}
                    helperText={errors.salesperson}
                    fullWidth
                    displayEmpty
                    renderValue={(selected) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#999" }}>
                          Select Salesperson
                        </span>
                      )
                    }
                    sx={{ ...errorFieldStyle }}
                  >
                    <MenuItem value="John Doe">John Doe</MenuItem>
                    <MenuItem value="John Smith">John Smith</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {/* Third Row */}
            <Grid item xs={12}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Subject</Typography>
                <TextField
                  fullWidth
                  placeholder="Enter subject"
                  value={subject}
                  size="small"
                  onChange={(e) => {
                    setSubject(e.target.value);
                    const error = validateField('subject', e.target.value);
                    setErrors(prev => ({ ...prev, subject: error }));
                  }}
                  sx={{
                    ...styles.multilineTextField,
                    ...errorFieldStyle,
                    "& .MuiOutlinedInput-root": {
                      height: "76px",
                      borderRadius: "8px !important",
                    },
                  }}
                  error={!!errors.subject}
                  helperText={errors.subject}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={12}>
              <Box sx={styles.customerNotes}>
                <Typography>Customer Notes</Typography>
                <TextField
                  rows={2}
                  fullWidth
                  value={customerNotes}
                  sx={{
                    ...styles.multilineTextField,
                    marginTop: "8px !important",
                    "& .MuiOutlinedInput-root": {
                      height: "76px",
                      borderRadius: "8px",
                      "& fieldset": {
                        border: "1px solid #D9D9D9",
                      },
                      "&:hover fieldset": {
                        borderColor: "#28AA4A",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#28AA4A",
                      },
                    },
                  }}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                />
                {/* </Box> */}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* Items Section */}
      <Box>
        <Typography sx={{ ...styles.itemsTitle }}>Items & Services</Typography>
        <Box sx={styles.container}>
          <Box sx={styles.itemsSection}>
            {items.map((item, index) => {
              return (
                <Box key={index} sx={styles.itemRow}>
                  <Box sx={{ width: '22%', position: 'relative' }}>
                    <Autocomplete
                      id={`item-select-${index}`}
                      options={[
                        { _id: "add_new", title: "+ New Item" },
                        ...(itemList || []),
                      ]}
                      getOptionLabel={(option) => option?.title || ""}
                      value={itemList?.find((i) => i.title === item.name) || null}
                      onChange={(event, newValue) => {
                        if (newValue?._id === "add_new") {
                          navigate("/dashboard/addNewItem"); // redirect page
                          return;
                        }
                        if (!newValue) {
                          handleItemChange("", index, "name");
                          handleItemChange("", index, "invoiceQuantity");
                          return;
                        }
                        handleItemChange(newValue?.title || "", index, "name");
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={
                            option._id === "add_new"
                              ? { color: color.secondary }
                              : {}
                          }
                        >
                          {option.title}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Item Name"
                          size="small"
                          sx={{
                            ...styles.itemField, ...errorFieldStyle, display: "flex",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "#fff",
                              height: "41px",
                              "& fieldset": {
                                border: "1px solid #D9D9D9",
                              },
                            },
                          }}
                          error={!!errors.items[index]?.name}
                        />
                      )}
                    />
                    {errors.items[index]?.name && (
                      <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5 }}>
                        {errors.items[index]?.name}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ width: '22%', position: 'relative' }}>
                    <TextField
                      size="small"
                      placeholder="Account"
                      value={item.account}
                      onChange={(e) =>
                        handleItemChange(e.target.value, index, "account")
                      }
                      sx={{
                        ...styles.descriptionField,
                        ...errorFieldStyle,
                        display: "flex",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          height: "41px",
                          "& fieldset": {
                            border: "1px solid #D9D9D9",
                          },
                        },
                      }}
                      error={!!errors.items[index]?.account}
                      SelectProps={{
                        displayEmpty: true,
                        IconComponent: () => null,
                        renderValue: (selected) => {
                          if (!selected)
                            return <span style={{ color: "#999" }}>Account</span>;
                          return selected === "64f27b8e1234567890abcdef"
                            ? "John Doe"
                            : "Jane Smith";
                        },
                      }}
                    ></TextField>
                    {errors.items[index]?.account && (
                      <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5 }}>
                        {errors.items[index]?.account}
                      </Typography>
                    )}
                  </Box>

                  <Grid item xs={12} lg={6}>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ ...styles.customInput, width: "100%", position: 'relative' }}
                    >
                      <TextField
                        value={
                          items[index]?.invoiceQuantity === 0
                            ? 0
                            : items[index]?.invoiceQuantity
                        }
                        type="number"
                        placeholder="Quantity"
                        sx={{ ...styles.quality, ...errorFieldStyle }}
                        error={!!errors.items[index]?.quantity}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          const maxQuantity = items[index]?.quantity;

                          if (
                            !isNaN(value) &&
                            value >= 0 &&
                            value <= maxQuantity
                          ) {
                            handleItemChange(value, index, "invoiceQuantity");
                          } else if (e.target.value === "") {
                            handleItemChange("", index, "invoiceQuantity");
                          } else {
                            const validValue = value < 0 ? 0 : maxQuantity;
                            handleItemChange(
                              validValue,
                              index,
                              "invoiceQuantity"
                            );
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" variant="filled">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#DEDEDE",
                                  height: 41,
                                  p: 0,
                                  ...styles.inputBgBox,
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: 21,
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "end",
                                    fontWeight: "600",
                                    userSelect: "none",
                                    borderRadius: "8px",
                                  }}
                                >
                                  {items[index]?.quantity}
                                </Box>
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    <Box
                                      sx={{
                                        bgcolor: "white",
                                        p: 1,
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "10px",
                                          fontWeight: 600,
                                          color: "#373D3F",
                                          mb: 1,
                                        }}
                                      >
                                        Stock Details
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: "10px",
                                          color: "#373D3F",
                                          fontWeight: 400,
                                        }}
                                      >
                                        Stock on Hand :{" "}
                                        {items[index]?.quantity || "0"}
                                      </Typography>
                                    </Box>
                                  }
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "white !important",
                                        p: 0,
                                        border: "1px solid #878787 !important",
                                        boxShadow: "none !important",
                                        "& .MuiTooltip-arrow": {
                                          color: "white !important",
                                          "&::before": {
                                            border: "1px solid #878787",
                                            backgroundColor: "white !important",
                                          },
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    sx={{
                                      color: "#878787",
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {errors.items[index]?.quantity && (
                        <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5, position: 'absolute', bottom: '-20px' }}>
                          {errors.items[index]?.quantity}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Rate"
                      value={
                        items[index]?.rate === ""
                          ? ""
                          : String(items[index]?.rate)
                      }
                      onChange={(e) =>
                        handleItemChange(e.target.value, index, "rate")
                      }
                      sx={{
                        ...styles.rateField, ...errorFieldStyle,
                        display: "flex",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          height: "41px",
                          "& fieldset": {
                            border: "1px solid #D9D9D9",
                          },
                        },
                      }}
                      error={!!errors.items[index]?.rate}
                    />
                    {errors.items[index]?.rate && (
                      <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5, position: 'absolute', bottom: '-20px', width: '100%' }}>
                        {errors.items[index]?.rate}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    size="small"
                    placeholder="Amount"
                    value={
                      (items[index]?.rate || 0) *
                      (items[index]?.invoiceQuantity || 0) || ""
                    }
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0 }}>
                          $
                        </InputAdornment>
                      ),
                      sx: {
                        paddingLeft: "6px",
                        "& input": {
                          padding: "10px 6px",
                          textAlign: "left",
                          marginLeft: "1px",
                        },
                        display: "flex",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          height: "41px",
                          "& fieldset": {
                            border: "1px solid #D9D9D9",
                          },
                        },
                      },
                    }}
                    sx={{
                      width: "170px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        height: "41px",
                        "& fieldset": {
                          border: "1px solid #D9D9D9",
                        },
                      },
                    }}
                  />

                  {
                    index === 0 ? (
                      <IconButton onClick={handleAddItem} sx={styles.addButton}>
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        sx={styles.deleteButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box >

      {/* Summary Section */}
      < Box >
        <Typography sx={{ ...styles.itemsTitle, marginBottom: "20px" }}>
          Discount and Tax (%)
        </Typography>
        <Grid container spacing={0}>
          <Grid item xs={12} md={7}>
            <Box sx={styles.container4}>
              <Box
                sx={{
                  height: "140px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box>
                      <Typography
                        sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                      >
                        Discount
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Enter Discount"
                        value={discountPercentage || ""}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= 0 && value <= 100) {
                            setDiscountPercentage(value);
                          } else if (e.target.value === "") {
                            setDiscountPercentage(0);
                          }
                          const error = validateField('discount', value);
                          setErrors(prev => ({ ...prev, discount: error }));
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        sx={{
                          backgroundColor: "white",
                          ...errorFieldStyle,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px !important",
                          },
                        }}
                        error={!!errors.discount}
                        helperText={errors.discount}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography
                        sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                      >
                        Tax
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Enter Tax"
                        value={taxPercentage || ""}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value >= 0 && value <= 100) {
                            setTaxPercentage(value);
                          } else if (e.target.value === "") {
                            setTaxPercentage(0);
                          }
                          const error = validateField('tax', value);
                          setErrors(prev => ({ ...prev, tax: error }));
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        sx={{
                          backgroundColor: "white",
                          ...errorFieldStyle,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px !important",
                          },
                        }}
                        error={!!errors.tax}
                        helperText={errors.tax}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={styles.container3}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  height: "140px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ color: "#666" }}>Total</Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    ${calculateSubTotal().toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ color: "#666" }}>
                    Tax ({taxPercentage || 0}%)
                  </Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    ${calculateTaxValue().toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ color: "#666" }}>
                    Discount ({discountPercentage || 0}%)
                  </Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    -${calculateDiscountValue().toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 1,
                    borderTop: "1px solid #eee",
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>Grand Total</Typography>
                  <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                    ${calculateGrandTotal().toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box >

      {/* Action Buttons */}
      < Box sx={{ ...styles.container }}>
        <Box sx={styles.actionButtons}>
          <Button
            type="submit"
            variant="contained"
            sx={styles.draftButton}
            disabled={
              !customerId ||
              !subject ||
              !salesperson ||
              !createNoteDate ||
              !createNoteNumber ||
              !customerNotes ||
              !discountPercentage ||
              !taxPercentage
            }
          >
            Save as Draft
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={styles.saveButton}
            disabled={
              !customerId ||
              !subject ||
              !salesperson ||
              !createNoteDate ||
              !createNoteNumber ||
              !customerNotes ||
              !discountPercentage ||
              !taxPercentage
            }
          >
            Save
          </Button>
        </Box>
      </Box >
    </>
  );
};

export default EditNotesPage;
